import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import { cn } from '@/lib/utils';

interface GuestSelectorProps {
  value: string;
  onChange: (value: string) => void;
  maxGuests?: number;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

const GuestSelector = ({
  value,
  onChange,
  maxGuests = 6,
  className = '',
  placeholder = '1 huésped',
  disabled = false,
}: GuestSelectorProps) => {
  const deviceInfo = useDeviceDetection();
  const [localValue, setLocalValue] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);

  const normalizedValue = useMemo(() => {
    if (!value || value === '' || value === 'undefined' || value === 'null') {
      return '1';
    }
    const cleanValue = value.toString().replace(/[^\d]/g, '');
    const numericValue = parseInt(cleanValue) || 1;
    if (numericValue < 1) return '1';
    if (numericValue > maxGuests) return maxGuests.toString();
    return numericValue.toString();
  }, [value, maxGuests]);

  useEffect(() => {
    if (!isUpdating) {
      setLocalValue(normalizedValue);
    }
  }, [normalizedValue, isUpdating]);

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (disabled) return;

      setIsUpdating(true);
      setLocalValue(newValue);

      if (deviceInfo.isMobile && deviceInfo.isAndroid) {
        setTimeout(() => {
          try {
            onChange(newValue);
          } catch {
            setLocalValue('1');
            onChange('1');
          } finally {
            setIsUpdating(false);
          }
        }, 50);
      } else {
        try {
          onChange(newValue);
        } catch (error) {
          console.error('❌ Error in guest selector change:', error);
          setLocalValue('1');
          onChange('1');
        } finally {
          setIsUpdating(false);
        }
      }
    },
    [onChange, deviceInfo.isMobile, deviceInfo.isAndroid, disabled],
  );

  const selectKey = useMemo(() => {
    if (deviceInfo.isMobile && deviceInfo.isAndroid) {
      return `guest-select-${normalizedValue}-${Date.now() % 1000}`;
    }
    return `guest-select-${normalizedValue}`;
  }, [normalizedValue, deviceInfo.isMobile, deviceInfo.isAndroid]);

  const displayValue = isUpdating ? localValue : normalizedValue;

  return (
    <Select
      key={selectKey}
      value={displayValue}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        className={cn(
          'h-12 bg-white border-gray-200',
          disabled &&
            'cursor-not-allowed opacity-60 pointer-events-none select-none',
          className,
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>

      <SelectContent
        sideOffset={4}
        align="start"
        position="popper"
        className="z-50 bg-white"
      >
        {Array.from({ length: maxGuests }, (_, index) => {
          const guestCount = index + 1;
          const label =
            guestCount === 1 ? '1 huésped' : `${guestCount} huéspedes`;
          return (
            <SelectItem
              key={`guest-${guestCount}`}
              value={guestCount.toString()}
              disabled={disabled}
            >
              {label}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

export default GuestSelector;
